// Aggiornamento del componente DocumentsList.tsx

import React, { useState, useEffect } from "react";
import { Warehouse } from "../../types/Warehouse";
import { useFetchContext } from "../../context/FetchContext";
import { useToast } from "../../context/ToastContext";
import { IDataPagination } from "types/User";
import Select from "components/ui/Select";
import Table from "components/ui/Table";
import Pagination from "components/ui/Pagination";
import DocumentFilters from "./DocumentFilters";
import {
  DocumentStatus,
  DocumentType,
  DocumentsData,
} from "../../types/Document";
import moment from "moment";

// Aggiungiamo il componente Modal
import Modal from "components/ui/Modal"; // Assicurati che questo componente esista

interface DocumentsResponse {
  total: number;
  totalPages: number;
  items: DocumentsData[];
}

const getStatus = (status: string) => {
  switch (status) {
    case DocumentStatus.OPEN:
      return "APERTO";
    case DocumentStatus.CLOSED:
      return "COMPLETATO";
    case DocumentStatus.PENDING:
      return "ATTESA";
  }
  return "";
};

const getDocumentType = (type: string) => {
  switch (type) {
    case DocumentType.ACCEPTANCE:
      return "ACCETTAZIONE";
    case DocumentType.DISPATCH:
      return "EVASIONE";
  }
  return "";
};

const DocumentsList: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [documents, setDocuments] = useState<DocumentsData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("id");
  const [orderType, setOrderType] = useState<string>("DESC");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Filtri
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<string>("");

  // Stato per la modale
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentsData | null>(null);

  const {
    fetchData,
    state: { isLoading },
  } = useFetchContext();
  const { showToast } = useToast();

  useEffect(() => {
    const getWarehouses = async () => {
      try {
        const response = await fetchData<IDataPagination<Warehouse>>(
          "/warehouses"
        );
        if (response && response.data && response.data.items) {
          setWarehouses(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        showToast("Errore durante il caricamento dei magazzini", "error");
      }
    };

    getWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse, currentPage, orderBy, orderType]);

  const fetchDocuments = async () => {
    try {
      const queryParams = new URLSearchParams({
        warehouseId: selectedWarehouse,
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        searchKeyword: searchKeyword,
        orderType: orderType,
        orderBy: orderBy,
      });

      // Aggiungi i filtri solo se sono presenti
      if (statusFilter) {
        queryParams.append("status", statusFilter);
      }

      if (typeFilter) {
        queryParams.append("type", typeFilter);
      }

      if (dateFilter) {
        queryParams.append("date", dateFilter);
      }

      const response = await fetchData<DocumentsResponse>(
        `/documents?${queryParams.toString()}`
      );

      if (response && response.data) {
        setDocuments(response.data.items || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      showToast("Errore durante il caricamento dei documenti", "error");
      setDocuments([]);
      setTotalPages(0);
      setTotalItems(0);
    }
  };

  const handleWarehouseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedWarehouse(e.target.value);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchKeyword(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchDocuments();
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleSort = (column: string) => {
    if (orderBy === column) {
      setOrderType(orderType === "ASC" ? "DESC" : "ASC");
    } else {
      setOrderBy(column);
      setOrderType("ASC");
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Gestori per i filtri
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
  };

  const handleTypeFilterChange = (type: string) => {
    setTypeFilter(type);
  };

  const handleDateFilterChange = (date: string) => {
    setDateFilter(date);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchDocuments();
  };

  const handleResetFilters = () => {
    setStatusFilter("");
    setTypeFilter("");
    setDateFilter("");
    setCurrentPage(1);
    fetchDocuments();
  };

  // Gestore per l'apertura della modale con i dettagli del documento
  const handleRowClick = (document: DocumentsData) => {
    setSelectedDocument(document);
    setIsModalOpen(true);
  };

  // Funzione per raggruppare i prodotti per codice
  const groupProductsByCode = (products: any[] = []) => {
    const grouped = products.reduce((acc, product) => {
      const code = product.productCode;
      if (!acc[code]) {
        acc[code] = { ...product, count: 1 };
      } else {
        acc[code].count += 1;
      }
      return acc;
    }, {});

    return Object.values(grouped);
  };

  return (
    <div className="overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h2 className="text-lg font-medium text-gray-900">Lista Documenti</h2>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label
              htmlFor="warehouse"
              className="block text-sm font-medium text-gray-700"
            >
              Seleziona Magazzino
            </label>
            <Select
              id="warehouse"
              value={selectedWarehouse}
              onChange={handleWarehouseChange}
              className="mt-1"
              fullWidth
            >
              <option value="">Seleziona un magazzino</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label
              htmlFor="search"
              className="block text-sm font-medium text-gray-700"
            >
              Ricerca
            </label>
            <input
              type="text"
              id="search"
              value={searchKeyword}
              onChange={handleSearch}
              placeholder="Cerca per cliente o documento"
              className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <DocumentFilters
              statusFilter={statusFilter}
              typeFilter={typeFilter}
              dateFilter={dateFilter}
              onStatusChange={handleStatusFilterChange}
              onTypeChange={handleTypeFilterChange}
              onDateChange={handleDateFilterChange}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="text-center py-4">
            <svg
              className="animate-spin h-8 w-8 mx-auto text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="mt-2 text-gray-500">Caricamento dati...</p>
          </div>
        ) : !selectedWarehouse ? (
          <div className="text-center py-4">
            <p className="text-gray-500">
              Seleziona un magazzino per visualizzare i dati
            </p>
          </div>
        ) : (
          <>
            <Table
              data={documents || []}
              columns={[
                {
                  key: "id",
                  header: "ID",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm font-medium text-gray-900">
                      #{item.id}
                    </div>
                  ),
                },
                {
                  key: "externalDocument",
                  header: "Documento",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-900">
                      {item.externalDocument}
                    </div>
                  ),
                },
                {
                  key: "type",
                  header: "Tipo",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-500">
                      {getDocumentType(item.type)}
                    </div>
                  ),
                },
                {
                  key: "customerDisplayName",
                  header: "Cliente",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-900">
                      {item.customerDisplayName}
                    </div>
                  ),
                },
                {
                  key: "destinationDisplayName",
                  header: "Destinatario",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-700">
                      {item.destinationDisplayName}
                    </div>
                  ),
                },
                {
                  key: "date",
                  header: "Data",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-500">
                      {moment(item.date).format("DD/MM/YYYY")}
                    </div>
                  ),
                },
                {
                  key: "products",
                  header: "Prodotti",
                  sortable: false,
                  render: (item) => (
                    <div className="text-sm text-gray-500">
                      {item.products?.length || 0}
                    </div>
                  ),
                },
                {
                  key: "status",
                  header: "Stato",
                  sortable: true,
                  render: (item) => (
                    <div
                      className="px-2 py-1 text-xs font-medium rounded-full text-center"
                      style={{
                        backgroundColor:
                          item.status === DocumentStatus.OPEN
                            ? "#dbeafe"
                            : item.status === DocumentStatus.CLOSED
                            ? "#d1fae5"
                            : item.status === DocumentStatus.PENDING
                            ? "#fef3c7"
                            : "#e5e7eb",
                        color:
                          item.status === DocumentStatus.OPEN
                            ? "#1e40af"
                            : item.status === DocumentStatus.CLOSED
                            ? "#065f46"
                            : item.status === DocumentStatus.PENDING
                            ? "#92400e"
                            : "#374151",
                      }}
                    >
                      {getStatus(item.status)}
                    </div>
                  ),
                },
              ]}
              keyExtractor={(item) => `${item.id}`}
              emptyMessage="Nessun documento trovato"
              sortColumn={orderBy}
              sortDirection={orderType as "ASC" | "DESC"}
              onSort={handleSort}
              onRowClick={handleRowClick}
              rowClassName={() => "cursor-pointer"}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              summaryText={`Showing ${
                totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0
              } to ${Math.min(
                currentPage * pageSize,
                totalItems
              )} of ${totalItems} documenti`}
            />
          </>
        )}
      </div>

      {/* Modale per i dettagli del documento */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`Dettagli Documento #${selectedDocument?.id || ""}`}
        size="lg"
      >
        {selectedDocument && (
          <div>
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Documento</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedDocument.externalDocument}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Tipo</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {getDocumentType(selectedDocument.type)}
                </p>
              </div>

              {selectedDocument.customerDisplayName && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Cliente</h3>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedDocument.customerDisplayName}
                  </p>
                </div>
              )}
              {selectedDocument.destinationDisplayName && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Destinatario
                  </h3>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedDocument.destinationDisplayName}
                  </p>
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Descrizione Spedizione
                </h3>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedDocument.carriageDescription}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Data</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {moment(selectedDocument.date).format("DD/MM/YYYY")}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Royalty Prodotti
                </h3>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedDocument.products?.reduce((sum, product) => {
                    return sum + product.royalty;
                  }, 0)}{" "}
                  €
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Royalty Spedizione
                </h3>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedDocument.shipmentSupplierCost || 0} €
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Stato</h3>
                <div
                  className="mt-1 px-2 py-1 text-xs font-medium rounded-full text-center inline-block"
                  style={{
                    backgroundColor:
                      selectedDocument.status === DocumentStatus.OPEN
                        ? "#dbeafe"
                        : selectedDocument.status === DocumentStatus.CLOSED
                        ? "#d1fae5"
                        : selectedDocument.status === DocumentStatus.PENDING
                        ? "#fef3c7"
                        : "#e5e7eb",
                    color:
                      selectedDocument.status === DocumentStatus.OPEN
                        ? "#1e40af"
                        : selectedDocument.status === DocumentStatus.CLOSED
                        ? "#065f46"
                        : selectedDocument.status === DocumentStatus.PENDING
                        ? "#92400e"
                        : "#374151",
                  }}
                >
                  {getStatus(selectedDocument.status)}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-base font-medium text-gray-900 mb-3">
                Prodotti
              </h3>

              {selectedDocument.products &&
              selectedDocument.products.length > 0 ? (
                <div className="border rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Codice
                        </th>

                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Quantità
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupProductsByCode(selectedDocument.products).map(
                        (product: any, index: number) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {product.productCode}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {product.count}{" "}
                              {product.count === 1 ? "pz" : "pz"}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Nessun prodotto disponibile
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DocumentsList;
